import axios from 'axios';
import { getUnifiedAuth } from './session';
import { resourceApis } from './config';

const client = axios.create({ timeout: 20000 });
const labels = { customer: '客saas', merchant: '商saas' };
let cached = null;
let pending = null;

// 财务微应用暂未接入融合身份，仅在融合菜单中隐藏，并清理因此变空的目录。
const filterCmhubMenus = menus => menus.reduce((result, node) => {
  const path = node.menuUrl || node.menuPath || '-1';
  if (/^\/baseFinancial(?:\/|\?|#|$)/.test(path)) return result;
  const originalChildren = Array.isArray(node.children) ? node.children : [];
  const children = filterCmhubMenus(originalChildren);
  if (originalChildren.length && !children.length && path === '-1') return result;
  result.push({ ...node, children });
  return result;
}, []);

export const clearCmhubResources = () => {
  if (getUnifiedAuth()) localStorage.removeItem('menuList');
  cached = null;
  pending = null;
};

// 大菜单下保留各侧完整的原始层级，不提升或删除原一级菜单。
export const groupMenus = (menus, side) => {
  const groupCode = `cmhub:${side}`;
  const mapNodes = (nodes, parent, prefix, ancestors) => nodes.map((node, index) => {
    const key = `${prefix}:${index}`;
    return {
      ...node,
      authSide: side,
      originalMenuCode: node.code === undefined ? node.menuCode : node.code,
      menuCode: key,
      menuName: node.name || node.menuName || '',
      menuPath: node.menuUrl || node.menuPath || '-1',
      icon: node.icon || '',
      parentMenuCode: parent,
      ancestorMenuCodes: ancestors,
      children: mapNodes(Array.isArray(node.children) ? node.children : [], key, key, [...ancestors, key]),
    };
  });
  return {
    menuCode: groupCode,
    menuName: labels[side],
    menuPath: '-1',
    icon: side === 'customer' ? 'team' : 'shop',
    authSide: side,
    children: mapNodes(menus, groupCode, groupCode, [groupCode]),
  };
};

const requestSide = async (auth, side, kind) => {
  const identity = auth[side];
  const env = process.env.NODE_ENV === 'development' ? localStorage.getItem('curEnv') : '';
  let baseURL = `/boss${env ? `/${env}` : ''}`;
  if (side === 'customer' && auth.customerAuthMode === 'private') {
    baseURL = process.env.VUE_APP_CMHUB_CUSTOMER_BASE_URL;
    if (!baseURL) throw new Error('未配置私有客系统接口地址');
  }
  const response = await client({
    baseURL,
    method: 'post',
    url: resourceApis[side][kind],
    headers: { 'X-Auth-AppId': identity.appId, 'X-Auth-Token': identity.token },
    data: { tenantId: identity.tenantId, accountId: identity.accountId, appType: 1 },
  });
  const body = response.data;
  if (!body || String(body.errCode) !== '0') {
    throw new Error((body && body.message) || '资源加载失败');
  }
  if (!Array.isArray(body.data)) throw new Error('菜单或权限返回格式错误');
  return body.data;
};

export const loadCmhubResources = ({ force = false } = {}) => {
  const auth = getUnifiedAuth();
  if (!auth) return Promise.reject(new Error('请先登录客商融合系统'));
  const key = JSON.stringify([auth, localStorage.getItem('curEnv')]);
  if (!force && cached && cached.key === key && !cached.value.errors.length) return Promise.resolve(cached.value);
  if (!force && pending && pending.key === key) return pending.promise;
  const task = { key };
  task.promise = Promise.all(['customer', 'merchant'].map(async (side) => {
    if (!auth[side].available) {
      return {
        side, available: false, menus: [], permissions: [],
      };
    }
    try {
      const [menus, permissions] = await Promise.all([
        requestSide(auth, side, 'menus'), requestSide(auth, side, 'permissions'),
      ]);
      return {
        side, available: true, menus, permissions,
      };
    } catch (error) {
      return {
        side, available: false, menus: [], permissions: [], error: error.message,
      };
    }
  })).then((results) => {
    if (pending !== task || key !== JSON.stringify([getUnifiedAuth(), localStorage.getItem('curEnv')])) {
      throw new Error('登录身份已变化，请重新加载菜单');
    }
    const value = { menus: [], permissions: {}, errors: [] };
    results.forEach((result) => {
      value.permissions[result.side] = result.permissions;
      if (result.error) value.errors.push(`${labels[result.side]}：${result.error}`);
      const menus = filterCmhubMenus(result.menus);
      if (result.available && menus.length) value.menus.push(groupMenus(menus, result.side));
    });
    // 失败结果允许重新请求，不伪装成已加载的空权限。
    cached = { key, value };
    return value;
  }).finally(() => {
    if (pending === task) pending = null;
  });
  pending = task;
  return task.promise;
};

export const getCmhubPermissions = (side) => {
  const key = JSON.stringify([getUnifiedAuth(), localStorage.getItem('curEnv')]);
  return cached && cached.key === key ? cached.value.permissions[side] || [] : [];
};
